import { Controller } from "@hotwired/stimulus"
import { MeiliSearch, MeiliSearchRequestError, MeiliSearchApiError } from "meilisearch"
import { capitalize } from "global/javascript/utils"
import { fetchJson } from "../../global/javascript/fetch_json"

const DEBOUNCE_TIME_MS = 100
const TRACKING_DEBOUNCE_TIME_MS = 500
export default class extends Controller {
  static targets = [
    "input",
    "results",
    "resultApiTemplate",
    "resultNodeTemplate",
    "content",
    "searchTutorial",
    "keyboardNavigationTutorial",
    "caretTemplate",
    "form",
    "loader",
    "searchIcon",
    "moreResults",
    "errorTemplate",
    "noResultsTemplate",
    "resultsListTemplate",
    "dialogFooter"
  ]

  static values = {
    limit: Number,
    offset: Number,
    configurationUrl: String,
    trackUrl: String
  }

  initialize() {
    // Simple enough unique identifier for same search session
    this.identifierValue = Math.random().toString(16)
  }

  async connect() {
    this.tabindex = -1
    // This controller is used to be able to abort ongoing search request
    // while typing. If an XHR requests is ongoing and we type a new
    // letter, we abort the previous request before starting a new
    // search (cf performSingleSearch or performMultiSearch)
    this.abortController = new AbortController()
    await this.#initSearch()
  }

  async search(query, isNewSearch = true) {
    // Abort any running search before starting
    this.abortController.abort()
    this.abortController = new AbortController()

    this.#toggleLoader(true)
    try {
      if (isNewSearch) {
        this.#resetOffset()
      }
      if (this.searchIndexes.length > 1) {
        await this.#performMultiSearch(query, isNewSearch)
      } else {
        await this.#performSingleSearch(query, isNewSearch)
      }
      if (isNewSearch) {
        this.#trackQuery(query)
      }
      this.#incrementOffset()
    } catch(e) {
      await this.#handleKnownErrors(query, isNewSearch, e)
    }
    this.#toggleLoader(false)
  }

  async #trackQuery(query) {
    if (this.trackUrlValue) {
      clearTimeout(this.trackingTimeout)
      this.trackingTimeout = setTimeout(() => {
        const trackUrl = new URL(this.trackUrlValue)
        trackUrl.searchParams.append("uid", this.identifierValue)
        trackUrl.searchParams.append("query", query)
        fetchJson(trackUrl)
      }, TRACKING_DEBOUNCE_TIME_MS)
    }
  }

  async #handleKnownErrors(query, isNewSearch, error) {
    if (this.#isAbortError(error)) return

    const shouldRetry = !this.retried && this.#isExpiredTokenError(error)
    if (shouldRetry) {
      this.retried = true
      console.debug("[Bump.sh][Search v2] Retrying search due to an expired token")
      // Refresh search config to get a new token
      await this.#initSearch()
      // Retry search
      await this.search(query, isNewSearch)
      this.retried = false
    } else {
      this.#displayError(error)
    }
  }

  // Meilisearch API doesn't expose a specific error code when token
  // has expired. We thus need to parse the error message. This is not
  // ideal, but as of today (9th oct. 2025), the error payload looks
  // like this:
  //
  // {
  //   "message":"Tenant token expired. Was valid up to `1760010880` and we're now `1760010889`.",
  //   "code":"invalid_api_key",
  //   "type":"auth",
  //   "link":"https://docs.meilisearch.com/errors#invalid_api_key"
  // }
  #isExpiredTokenError(error) {
    return error?.name === "MeiliSearchApiError" &&
      error?.cause?.code === "invalid_api_key" &&
      error?.message?.startsWith("Tenant token expired")
  }

  #isAbortError(error) {
    return error?.name === "AbortError" || (
      error?.cause instanceof DOMException &&
        error.cause.name === "AbortError"
    )
  }

  disableEnterKey(event) {
    if(event.keyCode == 13) {
      event.preventDefault()
      return false
    }
  }

  submitOnType() {
    clearTimeout(this.timeout)
    if (this.inputTarget.value.length > 0) {
      this.timeout = setTimeout(() => {
        this.search(this.inputTarget.value)
      }, DEBOUNCE_TIME_MS)
    } else {
      this.#resetDialog()
      this.#showSearchTutorial()
      this.#showDialogFooter()
    }
  }

  async onFocus() {
    this.#resetDialog()
    this.inputTarget.value = ""
    this.inputTarget.focus()
    this.#showSearchTutorial()
    this.#hideKeyboardNavigationTutorial()
  }

  #resetDialog() {
    this.#clearResults()
    this.#showDialogFooter()
    this.#hideShowMoreButton()
    this.#hideKeyboardNavigationTutorial()
  }

  previousElement(event) {
    event.preventDefault()
    const results = this.resultsTarget.querySelectorAll(".result-link")
    if (this.tabindex - 1 < 0) {
      this.inputTarget.focus()
      this.tabindex = -1
    } else {
      const focus = results[this.tabindex -= 1]
      this.#setFocus(focus)
    }
  }

  nextElement(event) {
    event.preventDefault()
    const results = this.resultsTarget.querySelectorAll(".result-link")
    if (this.tabindex + 1 < results.length) {
      const focus = results[this.tabindex += 1]
      this.#setFocus(focus)
    }
  }

  #hideShowMoreButton(event) {
    // this condition hides the more results button when
    // - the user clicks on ESC in the UI
    // - the user clicks on Dialog backdrop
    // - the user types ESC in the input field
    if (!event || event.target === event.currentTarget || (event.type === "keydown" && event.code === "Escape")) {
      this.moreResultsTarget.classList.add("hidden")
    }
  }

  #showShowMoreButton() {
    this.moreResultsTarget.classList.remove("hidden")
  }

  #hideKeyboardNavigationTutorial() {
    this.keyboardNavigationTutorialTarget.classList.add("hidden")
  }

  #showKeyboardNavigationTutorial() {
    this.keyboardNavigationTutorialTarget.classList.remove("hidden")
  }

  #hideSearchTutorial() {
    this.searchTutorialTarget.classList.add("hidden")
  }

  #showSearchTutorial() {
    this.searchTutorialTarget.classList.remove("hidden")
  }

  #hideDialogFooter() {
    this.dialogFooterTarget.classList.add("hidden")
  }

  #showDialogFooter() {
    this.dialogFooterTarget.classList.remove("hidden")
  }

  #setFocus(element) {
    element.focus()
    element.scrollIntoView({ block: "center", behavior: "instant" })
  }

  loadMoreResults(e) {
    e.preventDefault()
    this.search(this.inputTarget.value, false)
  }

  async #initSearch() {
    await this.#setConfig()
    this.#setClient()
    this.#setIndexes()
  }

  async #setConfig() {
    try {
      const response = await fetch(
        this.configurationUrlValue,
        {signal: this.abortController.signal}
      )
      const data = await response.json()
      this.config = data
    } catch (error) {
      if (this.#isAbortError(error)) return

      alert(`Error setting config: ${error}`)
    }
  }

  #setClient() {
    this.client = new MeiliSearch({
      host: this.config.host,
      apiKey: this.config.api_key,
      httpClient: this.#customHttpClient
    })

    return this.client
  }

  #setIndexes() {
    if (this.searchIndexes) return this.searchIndexes

    this.indexes = this.config.indexes
    this.searchIndexes = this.config.search_indexes
  }

  // Check whether the current resource index returns a result or not.
  // Based on Meilisearch Api or Hub index (depending on which page
  // we're on).
  async #isUpToDate(indexUid) {
    const indexName = this.indexes.find(index => index.startsWith(indexUid))
    const index = this.client.index(indexName)

    const response = await index.search(
      "",
      {},
      {signal: this.abortController.signal}
    )

    return (response.estimatedTotalHits > 0)
  }

  // single index search
  // allows for a single index to be searched
  //
  // Used by the doc (Api) page
  async #performSingleSearch(query, isNewSearch = true) {
    const results = await this.client.
      index(this.searchIndexes[0]).
      search(
        query,
        {
          attributesToCrop: ["description", "subtitle"],
          cropMarker: "...",
          cropLength: 100,
          attributesToHighlight: ["name", "description", "subtitle", "breadcrumbs"],
          highlightPreTag: "<span class=\"search-highlight\">",
          highlightPostTag: "</span>",
          offset: this.offsetValue,
          limit: this.limitValue
        },
        {signal: this.abortController.signal}
      )

    if (results.hits.length === 0) {
      if (await this.#isUpToDate("Api")) {
        this.#displayNoResults(query)
      } else if (!this.retried) {
        this.retried = true
        console.debug("[Bump.sh][Search v2] Retrying search due empty results")
        // Re-initialize search state (by fetching search config on our
        // backend)
        await this.#initSearch()
        // Retry search
        await this.search(query, isNewSearch)
        this.retried = false
      } else {
        console.debug(`[Bump.sh][Search v2] Missing Api metadata (${document.body.dataset.docRootUrl}). If the problem persists, please contact hello@bump.sh for support.`)
        this.#displayNoResults(query)
      }
      return
    }

    const resultsList = this.#createResultsList()
    this.#insertResults(resultsList, isNewSearch)
    this.#toggleMoreResults(results)

    results.hits.forEach(hit => {
      const resultNode = this.#hydrateNodeTemplateWithHit(hit)
      resultsList.appendChild(resultNode)
    })

    return results
  }

  // federated search
  // allows for multiple indexes to be searched at once
  //
  // Used by the Hub page
  async #performMultiSearch(query, isNewSearch = true) {
    // Make sure we have a valid tenant token
    if (!await this.#isUpToDate("Hub")) {
      // Re-initialize search state (by fetching search config on our
      // backend)
      await this.#initSearch()
    }

    const results = await this.client.multiSearch(
      {
        federation: {
          offset: this.offsetValue,
          limit: this.limitValue
        },
        queries: this.searchIndexes.map(index => this.#generateQueryConfig(query, index))
      },
      {signal: this.abortController.signal}
    )

    const resultsList = this.#createResultsList()
    this.#insertResults(resultsList, isNewSearch)
    this.#toggleMoreResults(results)

    results.hits.forEach(hit => {
      const hitType = hit._federation.indexUid.startsWith("Node") ? "node" : "api"
      const resultNode =
        hitType === "node" ?
          this.#hydrateNodeTemplateWithHit(hit, "hub") :
          this.#hydrateApiTemplateWithHit(hit)
      resultsList.appendChild(resultNode)
    })

    if (results.hits.length === 0) {
      this.#displayNoResults(query)
      return
    }

    return results
  }

  #insertResults(results, isNewSearch) {
    this.#hideSearchTutorial()
    this.#showKeyboardNavigationTutorial()
    this.#showDialogFooter()

    if (isNewSearch) {
      this.resultsTarget.replaceChildren(results)
    } else {
      this.resultsTarget.appendChild(results)
    }
  }

  #generateQueryConfig(query, index) {
    const attributesToCrop = []
    const attributesToHighlight = []
    const federationOptions = {}

    if (index.startsWith("Node")) {
      attributesToCrop.push("description", "subtitle")
      attributesToHighlight.push("name", "description", "subtitle", "breadcrumbs")
    } else if (index.startsWith("Api")) {
      attributesToCrop.push("description_from_current_definition")
      attributesToHighlight.push("name", "description_from_current_definition")
      // Promote results from the 'Api' index by adding a weight on
      // the federation options. (Default is 1.0)
      //
      // See https://www.meilisearch.com/docs/learn/multi_search/performing_federated_search#promote-results-from-a-specific-index for details
      federationOptions["weight"] = 1.2
    }

    return {
      attributesToCrop,
      cropMarker: "...",
      cropLength: 100,
      attributesToHighlight,
      highlightPreTag: "<span class=\"search-highlight\">",
      highlightPostTag: "</span>",
      indexUid: index,
      q: query,
      federationOptions
    }
  }

  #createResultsList() {
    const resultsList = document.importNode(this.resultsListTemplateTarget.content, true)
    return resultsList.querySelector(".doc-search_v2__results-list")
  }

  #hydrateNodeTemplateWithHit(hit, context = "node") {
    const resultNode = document.importNode(this.resultNodeTemplateTarget.content, true)
    this.#setResultLinkForTarget(resultNode, hit)

    const apiLabel = resultNode.querySelector(".result-api-name")
    if (hit.api_name && context === "hub") {
      apiLabel.innerHTML = hit.api_name
    } else {
      apiLabel.remove()
    }

    if (!this.#emptyArray(hit._formatted.breadcrumbs)) {
      // wrap last element in a span
      const formattedBreadcrumbs = this.#rejectContentType(hit._formatted.breadcrumbs)
      const lastElement = formattedBreadcrumbs.pop()

      let lastElementContent = lastElement
      if (hit.node_type) {
        lastElementContent = `${lastElement} <span class="result-node-type">${this.#formatNodeType(hit.node_type, hit.subtitle)}</span>`
      }
      const lastElementSpan = `<span class="result-breadcrumbs-last">${lastElementContent}</span>`

      // wrap highlighted elements in a span
      const wrappedFormattedBreadcrumbs = formattedBreadcrumbs.map(element => {
        if (element.includes("search-highlight")) {
          return `<span class="result-breadcrumbs-item">${element}</span>`
        } else {
          return element
        }
      })
      wrappedFormattedBreadcrumbs.push(lastElementSpan)

      const resultBreadcrumbs = wrappedFormattedBreadcrumbs.join(`<span class="result-breadcrumbs-caret">${this.caretTemplateTarget.innerHTML}</span></span>`)

      resultNode.querySelector(".result-breadcrumbs").innerHTML = resultBreadcrumbs
    }

    if (hit._formatted.name && this.#emptyArray(hit.breadcrumbs)) {
      resultNode.querySelector(".result-title").innerHTML = hit._formatted.name
    } else {
      resultNode.querySelector(".result-title").remove()
    }

    if (hit._formatted.description) {
      resultNode.querySelector(".result-description").innerHTML = hit._formatted.description
    } else {
      resultNode.querySelector(".result-description").remove()
    }

    const resultLabel = resultNode.querySelector(".result-label")
    if (hit.label) {
      const shortVerb = ["subscribe", "publish"].includes(hit.label)
        ? hit.label.substring(0, 3)
        : hit.label

      resultLabel.textContent = shortVerb.toUpperCase()
      resultLabel.classList.add(hit.label.toLowerCase())
    } else {
      resultLabel.remove()
    }

    const resultPath = resultNode.querySelector(".result-path")
    if (hit._formatted.subtitle && hit.node_type === "Endpoint") {
      resultPath.innerHTML = this.#formatSubtitleAsPath(hit._formatted.subtitle)
    } else {
      resultPath.remove()
    }

    const pathLabel = resultNode.querySelector(".result-path-label")
    if (!hit.label && (!hit._formatted.subtitle || hit.node_type !== "Endpoint") && pathLabel) {
      pathLabel.remove()
    }

    return resultNode
  }

  #hydrateApiTemplateWithHit(hit) {
    const resultNode = document.importNode(this.resultApiTemplateTarget.content, true)
    this.#setResultLinkForTarget(resultNode, hit)

    if (hit._formatted.name) {
      resultNode.querySelector(".result-title").innerHTML = hit._formatted.name
    }

    if (hit._formatted.description_from_current_definition) {
      resultNode.querySelector(".result-description").innerHTML = hit._formatted.description_from_current_definition
    }

    resultNode.querySelector(".result-label").remove()

    return resultNode
  }

  #setResultLinkForTarget(resultNode, hit) {
    const resultLink = resultNode.querySelector(".result-link")
    const documentationUrl = document.body.dataset.docRootUrl
    let baseUrl = documentationUrl || this.#fetchDocumentationUrlFromHub(hit.api_id) || hit.url

    // Add a trailing slash to base urls
    if (baseUrl.substr(-1) !== "/") baseUrl += "/"

    let hitUrl = hit.url
    // Prepend a '.' to relative URLs
    if (hitUrl.substr(0, 1) === "/") hitUrl = "." + hitUrl

    // Note: trailing slash on base URLs + ./ in front of relative
    // URLs is to make sure the path concatenation from base URL and
    // relative URL works well with the 'URL' JS lib.
    //
    // Handles both case if hit.url is a relative URL or an aboslute
    // URL. E.g.
    //
    // new URL("./operation/one", "https://bump.sh/coucou/hub/coucou/")
    // -> "https://bump.sh/coucou/hub/coucou/operation/one"
    //
    // or
    //
    // new URL("https://bump.sh/coucou/hub/coucou/doc/coucou", "https://bump.sh/coucou/hub/coucou/")
    // -> "https://bump.sh/coucou/hub/coucou/doc/coucou"
    resultLink.setAttribute("href", (new URL(hitUrl, baseUrl)).toString())
  }

  #fetchDocumentationUrlFromHub(apiId) {
    const api = document.querySelector(`#api-${apiId}`)
    return api?.href
  }

  #clearResults() {
    this.tabindex = -1
    this.#toggleLoader(false)
    this.resultsTarget.replaceChildren()
    this.#resetOffset()
  }

  #displayNoResults(query) {
    this.#hideShowMoreButton()
    this.#hideKeyboardNavigationTutorial()
    this.#showSearchTutorial()
    this.#hideDialogFooter()
    const noResultsNode = document.importNode(this.noResultsTemplateTarget.content, true)
    noResultsNode.querySelector(".result-query").textContent = query
    this.resultsTarget.replaceChildren(noResultsNode)
  }

  #displayError(error) {
    this.#hideShowMoreButton()
    this.#hideKeyboardNavigationTutorial()
    this.#showSearchTutorial()
    this.#hideDialogFooter()
    console.log(error)
    const errorNode = document.importNode(this.errorTemplateTarget.content, true)
    errorNode.querySelector(".error-message").textContent = error.message
    this.resultsTarget.replaceChildren(errorNode)
  }

  #toggleLoader(toggle) {
    this.loaderTarget.setAttribute("aria-hidden", !toggle)
    this.loaderTarget.setAttribute("aria-busy", toggle)
    this.searchIconTarget.setAttribute("aria-hidden", toggle)
  }

  #emptyArray(array) {
    return array === undefined || Array.isArray(array) && array.length === 0
  }

  #formatNodeType(nodeType, subtitle) {
    let type = nodeType.split("::").pop()

    if (type === "Property" && subtitle) {
      return capitalize(subtitle)
    } else {
      return type
    }
  }

  #formatSubtitleAsPath(subtitle) {
    // remove the method from the subtitle
    return subtitle.replace(/(GET|POST|PUT|PATCH|DELETE|OPTIONS|HEAD|TRACE|QUERY|SUBSCRIBE|PUBLISH)/, "")
  }

  #rejectContentType(breadcrumbs) {
    return breadcrumbs.filter((breadcrumb) => {
      if (!breadcrumb) return false
      return !breadcrumb.startsWith("application/") &&
        !breadcrumb.startsWith("multipart/") &&
        !breadcrumb.startsWith("text/")
    })
  }

  #toggleMoreResults(results) {
    // if there are more results to load, show the more results button
    if ((results.estimatedTotalHits - this.offsetValue - results.hits.length) > 0) {
      this.#showShowMoreButton()
    } else {
      this.#hideShowMoreButton()
    }
  }

  #resetOffset() {
    this.offsetValue = 0
  }

  #incrementOffset() {
    this.offsetValue += this.limitValue
  }

  // This is a custom implementation of the HTTP client defined in
  // the Meilisearch JS lib to be able to remove the
  // "x-meilisearch-client" request header.
  //
  // All the code is copied (and adjusted) from
  // https://github.com/meilisearch/meilisearch-js/blob/01f654ed63fa335af1d0d5c1ef7ff79186a7db19/src/http-requests.ts#L240-L273
  async #customHttpClient(url, init) {
    let response
    let responseBody

    // Remove "X-Meilisearch-Client" header
    init.headers?.delete("x-meilisearch-client")
    try {
      response = await fetch(url, init)
      responseBody = await response.text()
    } catch (error) {
      // We don't handle abort error here
      if (this.#isAbortError(error)) {
        return undefined
      }
      throw new MeiliSearchRequestError(
        url.toString(),
        error
      )
    }

    const parsedResponse =
      responseBody === "" ? undefined : JSON.parse(responseBody)

    if (!response.ok) {
      throw new MeiliSearchApiError(
        response,
        parsedResponse,
      )
    }

    return parsedResponse
  }
}
